/**
 * useCookies composable
 * Notes:
 *  - Only non-httpOnly cookies are reachable from JS. Cookies the server marks
 *    httpOnly (access_token, refresh_token, …) are invisible here by design.
 *  - To delete a cookie the browser requires the SAME path/domain it was set
 *    with — pass the matching options to `remove()`, or use `inferDomain` so a
 *    cross-subdomain cookie (e.g. set on `.assessfirst.info`) is targeted.
 */

export type SameSite = 'Strict' | 'Lax' | 'None';

export type CookieOptions = {
  /** Cookie path. Defaults to '/'. */
  path?: string;
  /** Lifetime in seconds. Omit for a session cookie. */
  maxAge?: number;
  /** Explicit domain attribute (e.g. '.assessfirst.info'). */
  domain?: string;
  /**
   * When true and no explicit `domain` is given, infer the root domain from the
   * current hostname so the cookie is shared across subdomains
   * (auth.assessfirst.info → .assessfirst.info). Ignored on localhost.
   */
  inferDomain?: boolean;
  /** SameSite policy. Defaults to 'Lax'. */
  sameSite?: SameSite;
  /** Force the Secure flag. Defaults to true on https, false otherwise. */
  secure?: boolean;
};

export function useCookies() {
  /** Read a cookie's decoded value, or null if absent. */
  function get(name: string): string | null {
    const prefix = `${name}=`;
    const raw = document.cookie.split(';').find(c => c.trim().startsWith(prefix));
    if (!raw) return null;
    return decodeURIComponent(raw.trim().slice(prefix.length));
  }

  /** Read and JSON.parse a cookie, or null if absent/unparseable. */
  function getJSON<T>(name: string): T | null {
    const value = get(name);
    if (value === null) return null;
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }

  /** True if the cookie is present (even with an empty value). */
  function has(name: string): boolean {
    return document.cookie.split(';').some(c => c.trim().startsWith(`${name}=`));
  }

  function buildAttributes(options: CookieOptions = {}): string {
    const { path = '/', maxAge, sameSite = 'Lax' } = options;
    const secure = options.secure ?? location.protocol === 'https:';

    let domain = options.domain;
    if (!domain && options.inferDomain) {
      const parts = window.location.hostname.split('.');
      // Skip localhost / bare hosts — only infer a shareable parent domain.
      if (parts.length > 2) domain = `.${parts.slice(-2).join('.')}`;
    }

    let attrs = `; path=${path}`;
    if (domain) attrs += `; domain=${domain}`;
    if (maxAge !== undefined) attrs += `; Max-Age=${maxAge}`;
    attrs += `; SameSite=${sameSite}`;
    if (secure) attrs += '; Secure';
    return attrs;
  }

  /** Write a cookie. Value is URL-encoded; objects should be JSON.stringify'd first. */
  function set(name: string, value: string, options: CookieOptions = {}): void {
    document.cookie = `${name}=${encodeURIComponent(value)}${buildAttributes(options)}`;
  }

  /**
   * Expire a cookie. Pass the same path/domain (or `inferDomain`) it was created
   * with — otherwise the browser keeps the original cookie.
   */
  function remove(name: string, options: Omit<CookieOptions, 'maxAge'> = {}): void {
    document.cookie = `${name}=${buildAttributes({ ...options, maxAge: 0 })}`;
  }

  return { get, getJSON, has, set, remove };
}
